/**
 * HojaA4 — la cáscara del documento: cabecera con logos, contenido y pie.
 *
 * ES LA VISTA PREVIA, NO EL DOCUMENTO. Sirve para revisar el informe antes de emitirlo;
 * el PDF que se le manda a la compañía lo genera Playwright + Chromium en
 * `api/pdf/generate.js`, desde el template de `pdf_templates`.
 *
 * POR QUÉ NO SE IMPRIME DESDE ACÁ. `window.print()` depende del navegador del operador y
 * de sus márgenes, no deja registro en `informes_accesos`, no puede subir a storage ni
 * mandarse por correo, y no es llamable desde un proceso. Dos caminos para producir el
 * mismo papel es una diferencia que aparece cuando la aseguradora reclama.
 *
 * @see api/pdf/_informeLiquidacion.js — el mismo documento, para el PDF
 * @see src/components/payments/yoolpay/informe/documento.js — tokens y formateadores
 */
import React from 'react';

import { TINTA } from './documento';

/**
 * Una celda de la rejilla de datos del encabezado.
 *
 * SIN VALOR NO SE RENDERIZA. Un rótulo con la celda vacía se lee como un dato que se
 * perdió; omitir el campo dice que no aplica. Es la regla del §7.3 del spec para los datos
 * del emisor, y vale igual para el resto.
 */
export function Dato({ rotulo, valor, bajada }) {
  if (valor == null || valor === '') return null;
  return (
    <div>
      <p
        style={{
          margin: '0 0 2px',
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: TINTA.rotulo,
        }}
      >
        {rotulo}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 10.5,
          lineHeight: 1.4,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {valor}
        {bajada ? (
          <>
            <br />
            <span style={{ color: TINTA.tenue }}>{bajada}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}

/** La rejilla de datos del encabezado, con sus líneas. */
export function RejillaDatos({ children, columnas = 4, borde = true }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnas}, 1fr)`,
        gap: '10px 16px',
        margin: '16px 0 0',
        padding: borde ? '12px 0' : '11px 0 0',
        borderTop: `1px solid ${borde ? TINTA.texto : TINTA.linea}`,
        ...(borde ? { borderBottom: `1px solid ${TINTA.linea}` } : {}),
      }}
    >
      {children}
    </div>
  );
}

/** Una caja con borde: notas, cuenta de destino, lectura del cuadro. */
export function Caja({ rotulo, children, acento, style }) {
  return (
    <div
      style={{
        breakInside: 'avoid',
        margin: '16px 0 0',
        padding: '11px 14px',
        border: `1px solid ${acento ? TINTA.marcaBorde : TINTA.caja}`,
        borderRadius: 8,
        ...(acento ? { background: TINTA.marcaFondo } : {}),
        ...style,
      }}
    >
      {rotulo ? (
        <p
          style={{
            margin: '0 0 5px',
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: acento ? TINTA.marca : TINTA.rotulo,
          }}
        >
          {rotulo}
        </p>
      ) : null}
      {children}
    </div>
  );
}

/** Un párrafo de nota, con la tipografía chica del documento. */
export function Nota({ children, style }) {
  return (
    <p style={{ margin: '0 0 5px', fontSize: 9.5, lineHeight: 1.6, color: '#3C3C3A', ...style }}>
      {children}
    </p>
  );
}

export default function HojaA4({ rotulo, eyebrow, titulo, children, pie }) {
  return (
    <article
      className="hoja-a4"
      style={{
        width: '210mm',
        maxWidth: '100%',
        margin: '0 auto 24px',
        padding: '18mm',
        background: '#fff',
        color: TINTA.texto,
        boxShadow: '0 1px 3px rgba(0,0,0,.10)',
        fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
        textWrap: 'pretty',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          paddingBottom: 7,
          borderBottom: `1px solid ${TINTA.linea}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/ds/assets/yool-logo.svg"
            alt="Yool"
            style={{ height: 15, width: 'auto', display: 'block' }}
          />
          <span style={{ width: 1, height: 14, background: TINTA.linea }} />
          <img
            src="/ds/assets/yoolpay-logo.svg"
            alt="YoolPay"
            style={{ height: 15, width: 'auto', display: 'block' }}
          />
        </div>
        <span
          style={{
            fontSize: 8.5,
            letterSpacing: '.05em',
            color: TINTA.rotulo,
            textTransform: 'uppercase',
            textAlign: 'right',
          }}
        >
          {rotulo}
        </span>
      </header>

      <section style={{ paddingTop: 18 }}>
        <p
          style={{
            margin: '0 0 4px',
            fontSize: 8.5,
            fontWeight: 700,
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: TINTA.marca,
          }}
        >
          {eyebrow}
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: 25,
            fontWeight: 700,
            letterSpacing: '-.02em',
            lineHeight: 1.15,
          }}
        >
          {titulo}
        </h1>
        {children}
      </section>

      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginTop: 24,
          paddingTop: 6,
          borderTop: `1px solid ${TINTA.linea}`,
          fontSize: 8.5,
          color: TINTA.pie,
        }}
      >
        <span>{pie}</span>
        <span>Documento de conciliación</span>
      </footer>
    </article>
  );
}
